import { Suspense, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProjectHub from './pages/ProjectHub';
import { Toaster } from '@/components/ui/sonner';
import AuthPage from './pages/AuthPage';
import { authClient } from '@/lib/auth';
import { getSettingsReturnRoute } from '@/lib/hash-route';

const PubmedScreen = lazy(() => import('./pages/PubmedScreen'));
const AIScreen = lazy(() => import('./pages/AIScreen'));
const PdfOcrScreen = lazy(() => import('./pages/PdfOcrScreen'));
const PdfAIScreen = lazy(() => import('./pages/PdfAIScreen'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));

const getCurrentRoute = () => {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return '/';
  return hash.startsWith('/') ? hash : `/${hash}`;
};

const normalizeHashRouterUrl = () => {
  const currentHash = window.location.hash;
  const normalizedHash = currentHash.startsWith('#/') ? currentHash : '#/';
  if (window.location.pathname !== '/' || currentHash !== normalizedHash) {
    window.history.replaceState(null, '', `/${normalizedHash}`);
  }
};

function App() {
  const { t } = useTranslation();
  const [route, setRoute] = useState(getCurrentRoute);
  const sessionState = authClient.useSession();
  const session = (
    sessionState.data || null
  ) as { user?: { email?: string; image?: string | null; name?: string | null; role?: string } } | null;

  useEffect(() => {
    normalizeHashRouterUrl();
    setRoute(getCurrentRoute());
    const onRouteChanged = () => setRoute(getCurrentRoute());
    window.addEventListener('hashchange', onRouteChanged);
    return () => window.removeEventListener('hashchange', onRouteChanged);
  }, []);

  if (sessionState.isPending) {
    return <div className="p-8 text-sm text-slate-500">{t('app.loadingSession')}</div>;
  }

  if (!session?.user) {
    return <AuthPage />;
  }

  const isAdmin = session.user.role === 'admin';
  const isSettingsOverlayRoute = route.startsWith('/settings');
  const renderedRoute = isSettingsOverlayRoute ? getSettingsReturnRoute(route) : route;
  const isAdminRoute = renderedRoute.startsWith('/admin/dashboard') || renderedRoute.startsWith('/admin/invitation-codes');
  const isProjectHubRoute = !(
    renderedRoute.startsWith('/pubmed') ||
    renderedRoute.startsWith('/pdf-ocr') ||
    renderedRoute.startsWith('/pdf-ai') ||
    renderedRoute.startsWith('/ai') ||
    isAdminRoute
  );
  const shouldMountSettingsHost = isSettingsOverlayRoute && !isProjectHubRoute;

  if (!isAdmin && isAdminRoute) {
    window.location.hash = '/';
    return <div className="p-8 text-sm text-slate-500">{t('app.redirecting')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="min-h-screen">
        {renderedRoute.startsWith('/pubmed') ? (
          <Suspense fallback={<div className="p-8 text-sm text-slate-500">{t('app.loading.pubmed')}</div>}>
            <PubmedScreen />
          </Suspense>
        ) : isAdminRoute ? (
          isAdmin ? (
            <Suspense fallback={<div className="p-8 text-sm text-slate-500">{t('app.loading.admin')}</div>}>
              <AdminDashboardPage />
            </Suspense>
          ) : (
            <div className="p-8 text-sm text-red-600">{t('app.forbiddenAdmin')}</div>
          )
        ) : renderedRoute.startsWith('/pdf-ocr') ? (
          <Suspense fallback={<div className="p-8 text-sm text-slate-500">{t('app.loading.pdfOcr')}</div>}>
            <PdfOcrScreen />
          </Suspense>
        ) : renderedRoute.startsWith('/pdf-ai') ? (
          <Suspense fallback={<div className="p-8 text-sm text-slate-500">{t('app.loading.pdfAi')}</div>}>
            <PdfAIScreen />
          </Suspense>
        ) : renderedRoute.startsWith('/ai') ? (
          <Suspense fallback={<div className="p-8 text-sm text-slate-500">{t('app.loading.ai')}</div>}>
            <AIScreen />
          </Suspense>
        ) : (
          <ProjectHub />
        )}
        {shouldMountSettingsHost ? (
          <div className="hidden">
            <ProjectHub />
          </div>
        ) : null}
      </main>
      <Toaster />
    </div>
  );
}

export default App;
